import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useForm, ValidationMode } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import BankAccountSchema from './BankAccountFormValidationSchema';
import { BankAccountInfoFields } from './BankAccountFormTypes';
import {
  sendMessage,
  handleTokenizationMessage,
  BankAccountEventTypes
} from '../PostMessageHelpers';
import ApplyTheme from '../ApplyTheme';
import ValidationError from '../ValidationError';

const BankAccount: FunctionComponent = (props) => {
  const location = useLocation();
  const formRef = useRef<HTMLFormElement>(null);
  const validationModeParam = new URLSearchParams(location.search).get('validationMode');
  const defaultValidationMode: keyof ValidationMode = 'onSubmit';
  const validationMode = (validationModeParam || defaultValidationMode) as keyof ValidationMode;
  const [styleOverrides, setStyleOverrides] = useState({});
  const { register, handleSubmit, formState: { errors }, trigger } = useForm({
    defaultValues: { ...new BankAccountInfoFields() },
    resolver: yupResolver(BankAccountSchema),
    mode: validationMode
  });

  useEffect(() => {
    window.addEventListener('message', handleMessageReceived);
    sendMessage({ eventType: BankAccountEventTypes.ready });

    return () => { window.removeEventListener('message', handleMessageReceived); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function sendHeightMessage() {
    const height = formRef.current?.clientHeight;
    if (!height) return;
    sendMessage({
      eventType: BankAccountEventTypes.resize,
      data: { height: height }
    });
  };

  useLayoutEffect(() => {
    sendHeightMessage();
  });

  async function handleMessageReceived(event: MessageEvent) {
    if (event.data.eventType === BankAccountEventTypes.validate) {
      const isValid = await trigger();
      sendMessage({ eventType: BankAccountEventTypes.validate, data: { isValid: isValid } });
    }
    if (event.data.eventType === BankAccountEventTypes.tokenize) {
      handleSubmit(
        (formData) => handleValidFormSubmit(event, formData),
        (errors) => handleInvalidFormSubmit(errors)
      )();
    }
    if (event.data.eventType === BankAccountEventTypes.styleOverrides) {
      setStyleOverrides(event.data.styleOverrides);
    }
    if (event.data.eventType === BankAccountEventTypes.resize) {
      sendHeightMessage();
    }
  }

  function handleValidFormSubmit(event: MessageEvent, formData: BankAccountInfoFields) {
    handleTokenizationMessage(event.data, formData);
  }

  function handleInvalidFormSubmit(errors: any) {
    // console.log(errors);
  }

  function getFormControlClassName(fieldName: keyof BankAccountInfoFields) {
    const hasError = errors[fieldName]?.message;
    const formControlClass = 'form-control';
    const validityClass = hasError ? 'error' : '';
    return [formControlClass, validityClass].join(' ');
  }

  return (
    <>
      <ApplyTheme styleOverrides={styleOverrides} />
      <form ref={formRef} className="layout">
        <div className="layout-row">
          <div className="layout-column">
            <label id="routing_number" htmlFor="routing_number" className="form-label">
              Routing Number
            </label>
            <input
              {...register('routing_number')}
              type="tel"
              placeholder=""
              maxLength={9}
              className={getFormControlClassName('routing_number')}
              aria-labelledby="routing_number"
              aria-required
            />
            <ValidationError message={errors.routing_number?.message} />
          </div>
        </div>

        <div className="layout-row">
          <div className="layout-column">
            <label id="account_number" htmlFor="account_number" className="form-label">
              Account Number
            </label>
            <input
              {...register('account_number')}
              type="tel"
              placeholder=""
              maxLength={17}
              autoCorrect='off'
              className={getFormControlClassName('account_number')}
              aria-labelledby="account_number"
              aria-required
            />
            <ValidationError message={errors.account_number?.message} />
          </div>
        </div>
      </form>
    </>
  );
};

export default BankAccount;
